const DASHBOARD_BASE_URL =
  (import.meta.env.VITE_DASHBOARD_URL as string) ??
  "https://dashboard.rockethealth.app";

export const WOMENS_HEALTH_CONSULT_STANDARD_ID =
  "d00001d1-0722-4cae-9dbe-78038d462eb8";
export const WOMENS_HEALTH_FOLLOWUP_STANDARD_ID =
  "c9fc513e-1413-4e35-8465-4aaa0086538c";

export const WOMENS_HEALTH_BUNDLE_IDS = [
  WOMENS_HEALTH_CONSULT_STANDARD_ID,
  WOMENS_HEALTH_FOLLOWUP_STANDARD_ID,
];

const buildLink = (path: string, usageId: string, includeUsageId = true) =>
  `${DASHBOARD_BASE_URL}${path}${includeUsageId ? `?usage_id=${usageId}` : ""}`;

export const isWomensHealthBundle = (standardId: string) =>
  WOMENS_HEALTH_BUNDLE_IDS.includes(standardId);

export const isWomensHealthFollowUpBundle = (standardId: string) =>
  standardId === WOMENS_HEALTH_FOLLOWUP_STANDARD_ID;

type WomensHealthSession = {
  name?: string | null;
  start_time?: string | Date | null;
  status?: string | null;
  is_unsuccessful?: boolean | null;
};

const isActiveSession = (status?: string | null) =>
  !status || status.toLowerCase() === "active";

// A session counts toward scheduling decisions only when it is active (not
// cancelled) and was not marked unsuccessful. Marking a session unsuccessful
// releases its bundle slot server-side and treats it as if it never happened,
// so the scheduler must ignore it too — otherwise an unsuccessful first consult
// keeps routing the primary link to a follow-up, leaving no way to re-book the
// consult.
const isCountableSession = (session: WomensHealthSession) =>
  isActiveSession(session.status) && !session.is_unsuccessful;

const getActiveConsultSessions = (sessions: WomensHealthSession[] = []) =>
  sessions.filter(
    (session) =>
      isCountableSession(session) &&
      session.name?.toLowerCase().includes("consult"),
  );

const getFirstConsultTime = (consultSessions: WomensHealthSession[] = []) => {
  const times = consultSessions
    .map((session) => new Date(session.start_time ?? "").getTime())
    .filter((time) => Number.isFinite(time));

  if (!times.length) return null;
  return Math.min(...times);
};

// Earliest start time across all countable (active, not-unsuccessful) sessions
// on the bundle.
const getFirstActiveSessionTime = (sessions: WomensHealthSession[] = []) => {
  const times = sessions
    .filter(isCountableSession)
    .map((session) => new Date(session.start_time ?? "").getTime())
    .filter((time) => Number.isFinite(time));

  if (!times.length) return null;
  return Math.min(...times);
};

// Classifies a follow-up bundle's booking:
//  - 'none' → no active session on the bundle yet (show a generic scheduling link)
//  - 'free' → within 30 days of the first active session AND sessions remaining
//  - 'paid' → otherwise
const resolveFollowUpKind = (
  sessions: WomensHealthSession[] = [],
  remainingSessions = 0,
): "none" | "free" | "paid" => {
  const firstSessionTime = getFirstActiveSessionTime(sessions);
  if (firstSessionTime === null) return "none";
  const isWithinThirtyDays =
    Date.now() <= firstSessionTime + 30 * 24 * 60 * 60 * 1000;
  return isWithinThirtyDays && remainingSessions > 0 ? "free" : "paid";
};

type WomensHealthPrimaryArgs = {
  standard_id: string;
  sessions?: WomensHealthSession[];
  remaining_sessions?: number;
};

type WomensHealthPrimaryResolution = {
  path: string;
  includeUsageId: boolean;
  /** Human-readable description of the link being copied. */
  label: string;
};

/**
 * Single source of truth for the women's-health "master" link. Mirrors the
 * men's-health logic: a follow-up bundle resolves to free or paid follow-up via
 * the 30-day rule (first session on the bundle); otherwise we point at
 * consultation until an active consult session exists, then switch to free
 * follow-up while within 30 days of the first consult (and sessions remain) or
 * paid follow-up afterwards. The link and label getters below both derive from
 * this so they never drift.
 */
function resolveWomensHealthPrimary({
  standard_id,
  sessions,
  remaining_sessions,
}: WomensHealthPrimaryArgs): WomensHealthPrimaryResolution {
  const consultPath = "/schedule/gynaecology-consultation";
  const followUpRoutes = {
    free: "/schedule/gynaecology-consultation-free-follow-up",
    paid: "/schedule/gynaecology-consultation-paid-follow-up",
  };

  // Follow-up bundle: free within 30 days of the first session on the bundle
  // (while sessions remain), otherwise paid. With no active session yet we can't
  // apply the 30-day rule, so surface a generic "Scheduling" link. usage_id is
  // included either way so the booking is attributed to this bundle usage.
  if (isWomensHealthFollowUpBundle(standard_id)) {
    const kind = resolveFollowUpKind(sessions, remaining_sessions ?? 0);
    if (kind === "none") {
      return { path: followUpRoutes.paid, includeUsageId: true, label: "Scheduling" };
    }
    const isFreeFollowUp = kind === "free";
    return {
      path: isFreeFollowUp ? followUpRoutes.free : followUpRoutes.paid,
      includeUsageId: true,
      label: isFreeFollowUp ? "Free Follow Up" : "Paid Follow Up",
    };
  }

  const consultSessions = getActiveConsultSessions(sessions);

  const hasSessions = (sessions ?? []).length > 0;
  if (!hasSessions) {
    return { path: consultPath, includeUsageId: true, label: "Consultation" };
  }

  if (consultSessions.length === 0) {
    return { path: consultPath, includeUsageId: true, label: "Consultation" };
  }

  const firstConsultTime = getFirstConsultTime(consultSessions);
  if (firstConsultTime === null) {
    return { path: consultPath, includeUsageId: true, label: "Consultation" };
  }

  const isWithinThirtyDays =
    Date.now() <= firstConsultTime + 30 * 24 * 60 * 60 * 1000;
  const remaining = remaining_sessions ?? 0;
  const isFreeFollowUp = isWithinThirtyDays && remaining > 0;

  const followUpPath = isFreeFollowUp
    ? followUpRoutes.free
    : followUpRoutes.paid;
  return {
    path: followUpPath,
    includeUsageId: isFreeFollowUp,
    label: isFreeFollowUp ? "Free Follow Up" : "Paid Follow Up",
  };
}

/** Primary link: consultation booking or follow-up depending on bundle type and usage. */
export function getWomensHealthPrimaryLink({
  standard_id,
  usage_id,
  sessions,
  remaining_sessions,
}: {
  standard_id: string;
  usage_id: string;
  sessions?: WomensHealthSession[];
  remaining_sessions?: number;
}) {
  const { path, includeUsageId } = resolveWomensHealthPrimary({
    standard_id,
    sessions,
    remaining_sessions,
  });
  return buildLink(path, usage_id, includeUsageId);
}

/** Label describing the link `getWomensHealthPrimaryLink` would produce for the same args. */
export function getWomensHealthPrimaryLabel(args: WomensHealthPrimaryArgs) {
  return resolveWomensHealthPrimary(args).label;
}

/**
 * Follow-up links for copying to clipboard.
 * Free follow-up includes usage_id (consumes from existing bundle).
 * Paid follow-up omits usage_id (user needs to purchase a new pack).
 */
export function getWomensHealthFollowUpLinks({ usage_id }: { usage_id: string }) {
  return {
    free: buildLink(
      "/schedule/gynaecology-consultation-free-follow-up",
      usage_id,
    ),
    paid: buildLink(
      "/schedule/gynaecology-consultation-paid-follow-up",
      usage_id,
      false,
    ),
  };
}
